import type { TextKey } from "./en.ts";

export const es: Record<TextKey, string> = {
  // --- SettingsMenu.tsx (theme + language + build commit) ---
  "theme.toDarkMode": "Cambiar a modo oscuro",
  "theme.toLightMode": "Cambiar a modo claro",
  "theme.light": "Claro",
  "theme.dark": "Oscuro",
  "common.language": "Idioma",
  "settings.title": "Ajustes",
  "settings.theme": "Tema",
  "settings.version": "Versión",

  // --- Landing.tsx ---
  "landing.title": "FLUIT Storm",
  "landing.tagline": "Modelado hidráulico de redes de drenaje urbano · compatible con SWMM 5",
  "landing.openButton": "Abrir fichero",
  "landing.newEmptyButton": "Crear proyecto",
  "landing.orTryAnExample": "O prueba con un ejemplo",
  "landing.sampleVictoriaName": "Victoria (Canadá)",
  "landing.sampleHagueName": "La Haya (Países Bajos)",
  "landing.sampleChaohuName": "Chaohu (China)",
  "landing.sampleBogotaName": "Bogotá (Colombia)",
  "landing.trySampleError": "No se ha podido cargar el ejemplo",
  "landing.openFileError": "No se ha podido abrir ese fichero: {message}",
  "landing.untitledProjectName": "Proyecto sin título",
  "landing.yourProjects": "Tus proyectos",
  "landing.savedProjectNotFoundTitle": "Proyecto no encontrado",
  "landing.savedProjectNotFoundMessage":
    "El proyecto al que apunta este enlace ya no existe en este navegador — puede que se haya eliminado, o que el enlace se abriera desde otro dispositivo o navegador.",
  "landing.deleteProject": "Eliminar {name}",
  "landing.newProjectTitle": "Nuevo proyecto",
  "landing.projectNameLabel": "Nombre del proyecto",
  "landing.back": "Atrás",
  "landing.open": "Abrir",

  // --- ProjectionPicker.tsx ---
  "projectionPicker.georeferencedLabel": "Este proyecto está georreferenciado",
  "projectionPicker.searchPlaceholder":
    "Busca por nombre o código EPSG (p. ej. «UTM 30N», «ETRS89», «25830»), o pega un string proj4",
  "projectionPicker.searching": "Buscando…",
  "projectionPicker.searchFailed": "Error en la búsqueda: {message}",
  "projectionPicker.hint": "Escribe para buscar y elige una proyección de la lista.",
  "projectionPicker.warning": "{reason} Se abrirá en modo plano salvo que elijas otra.",
  "projectionPicker.customProjection": "Proyección personalizada",

  // --- validateCrs.ts (shown via ProjectionPicker.tsx's warning above) ---
  "crsValidation.cantConvert": "Esta proyección no puede convertir estas coordenadas.",
  "crsValidation.invalidRange": "Estas coordenadas no parecen válidas para esta proyección.",
  "crsValidation.outsideAreaOfUse":
    "Estas coordenadas caen muy fuera del área de uso de esta proyección.",
  "crsValidation.generic": "Esto no parece correcto para estas coordenadas.",

  // --- App.tsx topbar ---
  "toolRail.label": "Herramientas",
  "toolbar.toggleElementList": "Mostrar/ocultar lista de elementos",
  "toolbar.search": "Buscar elemento",
  "toolbar.searchPlaceholder": "Nombre del elemento…",
  "toolbar.searchNoMatches": "Sin coincidencias",
  "toolbar.undo": "Deshacer",
  "toolbar.redo": "Rehacer",
  "toolbar.editGeometry": "Editar geometría",
  "toolbar.multiSelectMode": "Selección múltiple",
  "toolbar.selectionTools": "Herramientas de selección",
  "selectionTools.rectangle": "Rectángulo",
  "selectionTools.lasso": "Lazo",
  "selectionTools.byType": "Por tipo",
  "selectionTools.byQuery": "Por consulta",
  "toolbar.addElement": "Añadir elemento",
  "toolbar.addJunction": "Añadir pozo",
  "toolbar.addOutfall": "Añadir vertido",
  "toolbar.addStorage": "Añadir almacenamiento",
  "toolbar.addDivider": "Añadir divisor",
  "toolbar.addRaingage": "Añadir pluviómetro",
  "toolbar.addConduit": "Añadir conducto",
  "toolbar.addPump": "Añadir bomba",
  "toolbar.addOrifice": "Añadir orificio",
  "toolbar.addWeir": "Añadir vertedero",
  "toolbar.addOutlet": "Añadir descarga",
  "toolbar.addSubcatchment": "Añadir subcuenca",
  "toolbar.toPlaneView": "Cambiar a vista plana",
  "toolbar.toMapView": "Cambiar a vista de mapa",
  "toolbar.toMapViewOsmDisclosure":
    "La vista de mapa carga teselas de fondo desde OpenStreetMap, que ve la dirección IP de tu dispositivo y la ubicación aproximada de este proyecto.",
  "toolbar.runSimulation": "Ejecutar simulación",
  "toolbar.cancelSimulation": "Cancelar simulación",
  "simulation.failedTitle": "La simulación ha fallado",
  "simulation.failedMessage": "La simulación no se ha podido completar: {message}",
  "simulation.workerCrashedMessage":
    "La simulación se ha detenido de forma inesperada. Vuelve a intentarlo.",
  "simulation.phaseRunoff": "Escorrentía",
  "simulation.phaseRouting": "Calculando caudales",
  "simulation.phaseReport": "Generando informe…",
  "simulation.warningsTitle": "Simulación completada con {count} avisos",
  "simulation.warningsTitleOne": "Simulación completada con {count} aviso",
  "externalFiles.title": "Archivos externos necesarios",
  "externalFiles.hint":
    "Este proyecto lee datos de lluvia/clima de {count} archivos externos referenciados por su propio .inp, no incluidos en él. Aporta cada uno para usar sus datos reales, o ejecuta sin él (esa fuente reportará lluvia cero durante toda la simulación).",
  "externalFiles.hintOne":
    "Este proyecto lee datos de lluvia/clima de {count} archivo externo referenciado por su propio .inp, no incluido en él. Apórtalo para usar sus datos reales, o ejecuta sin él (esa fuente reportará lluvia cero durante toda la simulación).",
  "externalFiles.rowLabel": "{section} «{name}» → {filename}",
  "externalFiles.chooseFile": "Elegir archivo…",
  "externalFiles.provided": "Aportado: {fileName}",
  "externalFiles.replace": "Reemplazar…",
  "externalFiles.runButton": "Ejecutar simulación",
  "storage.errorTitle": "No se ha podido guardar",
  "storage.errorMessage":
    "Tus cambios no se han podido guardar en el almacenamiento del navegador: {message}. Deja esta pestaña abierta y exporta el proyecto (.inp) como copia de seguridad — recargar la página puede perder los cambios sin guardar.",
  "toolbar.simulationSummary": "Resumen de simulación",
  "toolbar.statisticsReport": "Informe de estadísticas",
  "toolbar.resultsAnimation": "Animar resultados sobre el mapa",
  "toolbar.resultsDashboard": "Dashboard de resultados",
  "toolbar.longitudinalProfile": "Perfil longitudinal",
  "toolbar.thematicMap": "Mapa temático",
  "toolbar.mapQuery": "Consulta de mapa",
  "toolbar.tools": "Herramientas",
  "toolbar.measureDistance": "Medir distancia",
  "measureTool.total": "Total:",
  "toolbar.georeference": "Georreferenciar por puntos de control",
  "georeference.title": "Georreferenciar por puntos de control",
  "georeference.hint":
    "Elige 2 o más nudos/pluviómetros y marca dónde están realmente en el mapa de abajo. Las coordenadas del proyecto se reescribirán en grados reales (WGS84) — esto no se deshace reabriendo esta herramienta, solo con el Deshacer normal.",
  "georeference.osmMapDisclosure":
    "El mapa de abajo carga teselas de fondo desde OpenStreetMap, que ve la dirección IP de tu dispositivo y la ubicación aproximada de este proyecto.",
  "georeference.pickElementPlaceholder": "Nudo o pluviómetro…",
  "georeference.noTargetYet": "Haz clic en el mapa",
  "georeference.targetCoords": "{lat}, {lon}",
  "georeference.residual": "±{meters} m",
  "georeference.removePoint": "Eliminar este punto de control",
  "georeference.addPoint": "Añadir punto",
  "georeference.apply": "Georreferenciar",
  "georeference.warningsTitle": "Algunas coordenadas no se han podido transformar",
  "malformedLinks.warningsTitle": "Se han descartado algunas filas de enlace mal formadas",
  "toolbar.exportInp": "Exportar .inp",
  "toolbar.closeProject": "Cerrar proyecto",
  "projectMenu.title": "Opciones del proyecto",
  "projectMenu.rename": "Cambiar nombre",
  "projectMenu.duplicate": "Guardar copia como",
  "projectMenu.delete": "Eliminar este proyecto",
  "projectMenu.renameTitle": "Cambiar nombre del proyecto",
  "projectMenu.duplicateTitle": "Guardar copia como",
  "projectMenu.copyNameSuffix": "{name} (copia)",

  // --- ExampleBanner.tsx ---
  "exampleBanner.message":
    "Estás viendo un ejemplo — los cambios no se guardan a menos que hagas una copia.",
  "sampleAttribution.hague":
    "Red de saneamiento de La Haya, Países Bajos (copia adaptada) — vía GNN-UDS, GPL-3.0.",
  "sampleAttribution.chaohu":
    "Red de drenaje de Chaohu, China (probablemente real, artículo de origen sin confirmar) — vía GNN-UDS, GPL-3.0.",
  "sampleAttribution.bogota":
    'Red de drenaje "Chico Sur", Bogotá, Colombia — vía MatSWMM, GPL-3.0.',
  "exampleBanner.sourceLink": "Fuente",
  "exampleBanner.attribution": "Atribución",
  "projectMenu.deleteTitle": "Eliminar proyecto",
  "projectMenu.deleteMessage":
    "«{name}» se eliminará de forma permanente. Esto no se puede deshacer.",
  "projectMenu.deleteConfirm": "Eliminar definitivamente",
  "projectMenu.simulationOptions": "Opciones de simulación",
  "projectMenu.elementDefaults": "Valores por defecto de nuevos elementos",
  "projectMenu.importLayers": "Importar capas GIS",
  "projectMenu.projectData": "Datos del proyecto",
  "projectMenu.backgroundImage": "Imagen de fondo",
  "projectData.groupData": "Datos comunes",
  "projectData.groupHydrology": "Hidrología",
  "projectData.groupHydraulics": "Hidráulica",
  "projectData.groupQuality": "Calidad",
  "projectData.groupControl": "Control",
  "projectData.tabCurves": "Curvas",
  "projectData.addCurve": "Añadir curva",
  "projectData.deleteCurve": "Eliminar curva",
  "projectData.noCurveSelected": "Selecciona una curva, o añade una nueva.",
  "projectData.curveType": "Tipo",
  "projectData.addPoint": "Añadir punto",
  "projectData.valuesTable": "Tabla de valores",
  "projectData.chartPreview": "Gráfico",
  "projectData.tabTimeSeries": "Series temporales",
  "projectData.addTimeSeries": "Añadir serie temporal",
  "projectData.deleteTimeSeries": "Eliminar serie temporal",
  "projectData.noTimeSeriesSelected": "Selecciona una serie temporal, o añade una nueva.",
  "projectData.date": "Fecha",
  "projectData.time": "Hora",
  "projectData.value": "Valor",
  "projectData.tabPatterns": "Patrones",
  "projectData.addPattern": "Añadir patrón",
  "projectData.deletePattern": "Eliminar patrón",
  "projectData.noPatternSelected": "Selecciona un patrón, o añade uno nuevo.",
  "projectData.patternType": "Tipo",
  "projectData.patternPeriod": "Periodo",
  "projectData.tabPollutants": "Contaminantes",
  "projectData.addPollutant": "Añadir contaminante",
  "projectData.deletePollutant": "Eliminar contaminante",
  "projectData.noPollutantSelected": "Selecciona un contaminante, o añade uno nuevo.",
  "projectData.pollutantUnits": "Unidades",
  "projectData.pollutantRainConc": "Concentración en lluvia",
  "projectData.pollutantGwConc": "Concentración en agua subterránea",
  "projectData.pollutantIiConc": "Concentración I&I",
  "projectData.pollutantDecayCoeff": "Coeficiente de decaimiento",
  "projectData.pollutantSnowOnly": "Solo en nieve",
  "projectData.pollutantCoPollutant": "Co-contaminante",
  "projectData.pollutantCoFraction": "Co-fracción",
  "projectData.pollutantDwfConc": "Concentración en tiempo seco",
  "projectData.pollutantInitConc": "Concentración inicial",
  "projectData.tabLandUses": "Usos del suelo",
  "projectData.addLandUse": "Añadir uso del suelo",
  "projectData.deleteLandUse": "Eliminar uso del suelo",
  "projectData.noLandUseSelected": "Selecciona un uso del suelo, o añade uno nuevo.",
  "projectData.sweepingInterval": "Intervalo de barrido (días)",
  "projectData.sweepingAvailability": "Disponibilidad de barrido",
  "projectData.lastSweptDay": "Último barrido (días antes del inicio)",
  "projectData.noPollutantsYet":
    "Añade primero un contaminante (pestaña Contaminantes) para configurar acumulación/lavado.",
  "projectData.tabBuildup": "Acumulación",
  "projectData.tabWashoff": "Lavado",
  "projectData.pollutant": "Contaminante",
  "projectData.buildupFunction": "Función",
  "projectData.washoffFunction": "Función",
  "projectData.coeff1": "Coef. 1",
  "projectData.coeff2": "Coef. 2",
  "projectData.coeff3": "Coef. 3",
  "projectData.normalizer": "Normalizador",
  "projectData.cleaningEfficiency": "Efic. limpieza",
  "projectData.bmpEfficiency": "Efic. BMP",
  "common.name": "Nombre",
  // Flujo de borrado — siempre la palabra de CATEGORÍA (nudo/enlace/...), nunca un tipo concreto (pozo/vertido/...).
  "category.node": "nudo",
  "category.link": "enlace",
  "category.subcatchment": "subcuenca",
  "category.raingage": "pluviómetro",
  "delete.confirmTitle": "Eliminar {category}",
  "delete.confirmMessage": '¿Eliminar {category} "{name}"?',
  "delete.blockedTitle": "No se puede eliminar {category}",
  // Un nudo bloqueado solo lista enlaces, nunca otra cosa — singular/plural
  // en vez de una sola plantilla genérica, para poder escribir "al enlace"
  // (con la contracción a+el correcta) en el caso normal de uno solo.
  "delete.blockedNodeConnectedOne": 'Nudo "{name}" sigue conectado al enlace {items}.',
  "delete.blockedNodeConnectedMany": 'Nudo "{name}" sigue conectado a los enlaces {items}.',
  "delete.blockedRaingageUsedOne": 'El pluviómetro "{name}" todavía lo usa la subcuenca {items}.',
  "delete.blockedRaingageUsedMany":
    'El pluviómetro "{name}" todavía lo usan las subcuencas {items}.',
  "delete.blockedOutletUsedOne": '"{name}" todavía se usa como salida de la subcuenca {items}.',
  "delete.blockedOutletUsedMany": '"{name}" todavía se usa como salida de las subcuencas {items}.',
  "delete.blockedControlRuleOne": '"{name}" todavía la referencia la regla de control {items}.',
  "delete.blockedControlRuleMany": '"{name}" todavía la referencian las reglas de control {items}.',
  // Un enlace borrado solo puede huerfanizar sus 2 nudos extremos como
  // mucho — mismo motivo de singular/plural que arriba.
  "delete.confirmLinkOrphanWarningOne":
    'Al eliminar enlace "{name}" también se eliminará el nudo {items}, que se quedaría sin conexiones. ¿Continuar?',
  "delete.confirmLinkOrphanWarningMany":
    'Al eliminar enlace "{name}" también se eliminarán los nudos {items}, que se quedarían sin conexiones. ¿Continuar?',
  "common.and": "y",
  "common.andNMore": "y {count} más",
  "delete.confirmButton": "Eliminar",
  "delete.blockedGroupTitle": "No se puede eliminar la selección",
  "delete.blockedGroupMessage":
    "{items} sigue conectado a un enlace fuera de la selección. Deselecciónalo, o selecciona también ese enlace.",
  "delete.confirmGroupTitle": "Eliminar {count} elementos",
  "delete.confirmGroupMessage": "¿Eliminar los {count} elementos seleccionados?",
  "delete.confirmGroupOrphanWarning":
    "Eliminar los {count} elementos seleccionados también eliminará {items}, que se quedarían sin conexiones. ¿Continuar?",
  "multiSelect.count": "{count} seleccionados",
  "multiSelect.groupEdit": "Editar selección",
  "multiSelect.delete": "Eliminar selección",
  "groupEdit.title": "Editar {count} Elementos Seleccionados",
  "groupEdit.noProperties":
    "Los elementos seleccionados no tienen ninguna propiedad editable en común.",
  "groupEdit.property": "Propiedad",
  "groupEdit.newValue": "Valor nuevo",
  "groupEdit.apply": "Aplicar a todos",
  "gisImport.title": "Importar capas GIS",
  "gisImport.hint":
    "Selecciona una carpeta — cada fichero .geojson/.json y cada shapefile (.shp+.dbf+.shx, se recomienda .prj) que contenga aparecerá abajo como una capa importable.",
  "gisImport.pickFolder": "Elegir carpeta",
  "gisImport.reading": "Leyendo carpeta…",
  "gisImport.readFailed": "No se pudo leer esa carpeta: {message}",
  "gisImport.layerReadFailed": "{fileName}: no se pudo leer ese fichero — {message}",
  "gisImport.noneFound": "No se ha encontrado ningún fichero .geojson/.json en esa carpeta.",
  "gisImport.columnFile": "Fichero",
  "gisImport.columnGeometry": "Geometría",
  "gisImport.columnFeatures": "Elementos",
  "gisImport.columnType": "Importar como",
  "gisImport.skip": "Omitir (no importar)",
  "gisImport.mixedGeometry": "Geometría mixta — se omite",
  "gisImport.nameField": "Nombre",
  "gisImport.useDefault": "Usar valor por defecto ({value})",
  "gisImport.importButton": "Importar",
  "gisImport.summaryLine": "{file}: {created} importados, {skipped} omitidos",
  "gisImport.placeholderSummary":
    "{count} elementos importados siguen con valores por defecto — busca la marca en la lista de elementos para localizarlos y completarlos.",
  "gisImport.placeholderSummaryOne":
    "{count} elemento importado sigue con un valor por defecto — busca la marca en la lista de elementos para localizarlo y completarlo.",
  "gisImport.missingPrj":
    "Falta el fichero .prj — se desconoce el sistema de coordenadas de origen, así que esta capa no se puede importar.",
  "gisImport.prjUnparseable":
    "No se ha podido leer la definición de sistema de coordenadas del fichero .prj — esta capa no se puede importar.",
  "gisImport.unknownProjectCrsBlocked":
    "Este proyecto ya tiene elementos pero no tiene un sistema de coordenadas asignado, así que las coordenadas reales de una capa importada no se pueden combinar con él de forma segura. Crea o abre el proyecto de nuevo asignando un sistema de coordenadas, y después importa.",
  "common.save": "Guardar",

  // --- OptionsDialog.tsx ---
  "optionsDialog.title": "Opciones de simulación",
  "optionsDialog.general": "General",
  "optionsDialog.flowUnits": "Unidades de caudal",
  "optionsDialog.flowRouting": "Método de rutado",
  "optionsDialog.minSlope": "Pendiente mínima de conducto (%)",
  "optionsDialog.simulationPeriod": "Periodo de simulación",
  "optionsDialog.startDate": "Fecha de inicio",
  "optionsDialog.startTime": "Hora de inicio",
  "optionsDialog.endDate": "Fecha de fin",
  "optionsDialog.endTime": "Hora de fin",
  "optionsDialog.reportStartDate": "Fecha de inicio del informe",
  "optionsDialog.reportStartTime": "Hora de inicio del informe",
  "optionsDialog.timeSteps": "Pasos de tiempo",
  "optionsDialog.reportStep": "Paso de informe",
  "optionsDialog.wetStep": "Paso en tiempo húmedo",
  "optionsDialog.dryStep": "Paso en tiempo seco",
  "optionsDialog.routingStep": "Paso de rutado",
  "optionsDialog.dynamicWave": "Onda dinámica",
  "optionsDialog.normalFlowLimited": "Caudal normal limitado por",
  "optionsDialog.allowPonding": "Permitir encharcamiento",
  "optionsDialog.variableStep": "Paso de tiempo variable (factor de seguridad)",
  "optionsDialog.minimumStep": "Paso de tiempo mínimo (seg)",
  "optionsDialog.maxTrials": "Máx. intentos por paso de tiempo",
  "optionsDialog.headTolerance": "Tolerancia de convergencia de carga",
  "optionsDialog.minSurfArea": "Área superficial mínima",
  "optionsDialog.lengtheningStep": "Paso de alargamiento de conducto",
  "defaultsDialog.title": "Valores por Defecto de Nuevos Elementos",
  "defaultsDialog.idLabels": "Etiquetas ID",
  "defaultsDialog.subcatchment": "Subcuenca",
  "defaultsDialog.nodeLink": "Nudos/Enlaces",
  "defaultsDialog.raingage": "Pluviómetros",
  "defaultsDialog.subcatchmentPrefix": "Subcuencas",
  "defaultsDialog.junction": "Pozos",
  "defaultsDialog.outfall": "Vertidos",
  "defaultsDialog.divider": "Divisores",
  "defaultsDialog.storage": "Depósitos",
  "defaultsDialog.conduit": "Conductos",
  "defaultsDialog.pump": "Bombas",
  "defaultsDialog.orifice": "Orificios",
  "defaultsDialog.weir": "Vertederos",
  "defaultsDialog.outlet": "Salidas",
  "defaultsDialog.idIncrement": "Incremento de ID",
  "defaultsDialog.area": "Área",
  "defaultsDialog.width": "Ancho",
  "defaultsDialog.slopePercent": "%Pendiente",
  "defaultsDialog.imperviousPercent": "%Imperm.",
  "defaultsDialog.nImperv": "N-Imperm.",
  "defaultsDialog.nPerv": "N-Perm.",
  "defaultsDialog.dStoreImperv": "S-Imperm.",
  "defaultsDialog.dStorePerv": "S-Perm.",
  "defaultsDialog.pctZeroImperv": "%Imperm. sin depresión",
  "defaultsDialog.invertElevation": "Cota de solera del nudo",
  "defaultsDialog.maxDepth": "Profundidad máx. del nudo",
  "defaultsDialog.pondedArea": "Área de encharcamiento del nudo",
  "defaultsDialog.conduitLength": "Longitud de conducto",
  "defaultsDialog.conduitRoughness": "Rugosidad de conducto",
  "toolbar.hintAddLinkWaiting": "{kind}: haz clic en el otro extremo (desde {name})",
  "toolbar.hintAddLinkStart": "{kind}: haz clic en el nudo inicial",
  "toolbar.hintAddNode": "{kind}: haz clic en el mapa para colocarlo",
  "toolbar.hintAddSubcatchment":
    "Haz clic en el mapa para dibujar su contorno ({count} puntos hasta ahora)",
  "toolbar.hintAddSubcatchmentOne":
    "Haz clic en el mapa para dibujar su contorno ({count} punto hasta ahora)",
  "toolbar.finishSubcatchment": "Finalizar",
  "toolbar.hintVertexIdle": "[ / ] elige un vértice, Insertar/+ añade el primero",
  "toolbar.hintVertexFocused":
    "Vértice {index}/{count} — flechas para mover, Insertar/+ para añadir otro, Supr para quitarlo, Esc para salir",
  "common.cancel": "Cancelar",
  "common.done": "Hecho",
  "common.ok": "Aceptar",
  "common.close": "Cerrar",
  "common.discardChangesTitle": "¿Descartar cambios?",
  "common.discardChangesMessage": "Tienes cambios sin guardar. Si cierras ahora, se perderán.",
  "common.discardChangesConfirm": "Descartar cambios",
  "common.keepEditing": "Seguir editando",

  // --- ElementBrowser.tsx ---
  "elementBrowser.raingages": "Pluviómetros",
  "elementBrowser.subcatchments": "Subcuencas",
  "elementBrowser.nodes": "Nudos",
  "elementBrowser.links": "Enlaces",
  "elementBrowser.hydrology": "Hidrología",
  "elementBrowser.toggleKindGroup": "Expandir/contraer {kind}",
  "elementBrowser.toggleCategoryGroup": "Expandir/contraer {category}",
  "elementBrowser.toggleOnMap": "Mostrar/ocultar en el mapa",
  "elementBrowser.filterPlaceholder": "Filtrar árbol…",
  "elementBrowser.filterNoMatches": "Ningún elemento coincide con este filtro",
  "elementBrowser.placeholderHint": "Importado con valores por defecto — pendiente de revisar",

  // --- PropertyPanel.tsx ---
  "propertyPanel.description": "Descripción",
  "propertyPanel.noProperties": "No hay propiedades registradas para este elemento.",
  "propertyPanel.coverages": "Usos del suelo",
  "propertyPanel.noLandUsesYet":
    "Añade primero un uso del suelo (Datos del proyecto → Usos del suelo) para asignar cobertura.",
  "propertyPanel.noCoverages": "Todavía no hay ningún uso del suelo asignado.",
  "propertyPanel.landUse": "Uso del suelo",
  "propertyPanel.percent": "Porcentaje",
  "propertyPanel.addCoverage": "Añadir uso del suelo",
  "propertyPanel.deleteCoverage": "Quitar uso del suelo",
  "propertyPanel.coveragesNotFull": "Suma {total}%, no 100%.",
  "propertyPanel.loadings": "Acumulación inicial de contaminante",
  "propertyPanel.noLoadings":
    "Sin acumulación inicial fijada — se usa la fórmula del tiempo de secado previo.",
  "propertyPanel.initialBuildup": "Acumulación inicial",
  "propertyPanel.addLoading": "Añadir acumulación",
  "propertyPanel.deleteLoading": "Quitar acumulación",
  "propertyPanel.treatment": "Tratamiento",
  "propertyPanel.noTreatments": "Todavía no hay ninguna expresión de tratamiento.",
  "propertyPanel.treatmentKind": "Tipo",
  "propertyPanel.treatmentKindRemoval": "Eliminación (R)",
  "propertyPanel.treatmentKindConcentration": "Concentración (C)",
  "propertyPanel.treatmentExpression": "Expresión",
  "propertyPanel.addTreatment": "Añadir tratamiento",
  "propertyPanel.deleteTreatment": "Quitar tratamiento",

  // --- ProjectDataDialog.tsx's LID Controls tab (LidControlsEditor.tsx) ---
  "projectData.tabLidControls": "Controles LID",
  "lid.addControl": "Añadir control LID",
  "lid.deleteControl": "Eliminar control LID",
  "lid.noControlSelected": "Selecciona un control LID, o añade uno nuevo.",
  "lid.type": "Tipo",
  "lid.typeBC": "Celda de bio-retención",
  "lid.typeRG": "Jardín de lluvia",
  "lid.typeGR": "Cubierta verde",
  "lid.typeIT": "Zanja de infiltración",
  "lid.typePP": "Pavimento poroso",
  "lid.typeRB": "Barril de lluvia",
  "lid.typeVS": "Cuneta vegetada",
  "lid.typeRD": "Desconexión de bajante",
  "lid.layerSurface": "Superficie",
  "lid.surfaceStorageHeight": "Altura del bordillo",
  "lid.surfaceVegFraction": "Fracción de vegetación",
  "lid.surfaceRoughness": "Rugosidad superficial (n de Manning)",
  "lid.surfaceSlope": "Pendiente superficial (%)",
  "lid.surfaceSideSlope": "Pendiente lateral (recorrido/desnivel)",
  "lid.layerSoil": "Suelo",
  "lid.soilThickness": "Espesor",
  "lid.soilPorosity": "Porosidad",
  "lid.soilFieldCap": "Capacidad de campo",
  "lid.soilWiltPoint": "Punto de marchitez",
  "lid.soilKSat": "Conductividad",
  "lid.soilKSlope": "Pendiente de conductividad",
  "lid.soilSuction": "Succión",
  "lid.layerStorage": "Almacenamiento",
  "lid.storageThickness": "Espesor",
  "lid.storageVoidRatio": "Relación de vacíos",
  "lid.storageKSat": "Conductividad",
  "lid.storageClogFactor": "Factor de colmatación",
  "lid.storageCovered": "Cubierto (sin evaporación)",
  "lid.layerPavement": "Pavimento",
  "lid.pavementThickness": "Espesor",
  "lid.pavementVoidRatio": "Relación de vacíos",
  "lid.pavementImpervFrac": "Fracción impermeable",
  "lid.pavementPermeability": "Permeabilidad",
  "lid.pavementClogFactor": "Factor de colmatación",
  "lid.pavementRegenDays": "Intervalo de regeneración (días)",
  "lid.pavementRegenDegree": "Grado de regeneración",
  "lid.layerDrain": "Drenaje",
  "lid.drainCoeff": "Coeficiente de caudal",
  "lid.drainExpon": "Exponente de caudal",
  "lid.drainOffset": "Altura de offset",
  "lid.drainDelay": "Retardo (h)",
  "lid.drainHOpen": "Altura de apertura",
  "lid.drainHClose": "Altura de cierre",
  "lid.drainCurve": "Curva de caudal",
  "lid.layerDrainMat": "Manta de drenaje",
  "lid.drainMatThickness": "Espesor",
  "lid.drainMatVoidRatio": "Relación de vacíos",
  "lid.drainMatRoughness": "Rugosidad (n de Manning)",
  "lid.layerRemovals": "Eliminaciones de contaminantes adicionales",
  "lid.noRemovals": "No hay ninguna tasa de eliminación adicional.",
  "lid.removalPercent": "Porcentaje eliminado",
  "lid.addRemoval": "Añadir eliminación",
  "lid.deleteRemoval": "Quitar",

  // --- PropertyPanel.tsx's LID usage sub-section (LidUsageEditor.tsx) ---
  "lidUsage.title": "Controles LID",
  "lidUsage.noControlsYet":
    "Añade primero un control LID (Datos del proyecto → Controles LID) para colocarlo aquí.",
  "lidUsage.noUsage": "Todavía no hay ningún control LID en esta subcuenca.",
  "lidUsage.lidControl": "Control LID",
  "lidUsage.number": "Número",
  "lidUsage.area": "Área (por unidad)",
  "lidUsage.width": "Ancho (por unidad)",
  "lidUsage.initSat": "Saturación inicial (%)",
  "lidUsage.fromImperv": "% de área impermeable tratada",
  "lidUsage.toPerv": "Enviar salida al área permeable",
  "lidUsage.fromPerv": "% de área permeable tratada",
  "lidUsage.drainTo": "Drenar a (nudo/subcuenca)",
  "lidUsage.addUsage": "Añadir colocación LID",
  "lidUsage.deleteUsage": "Quitar colocación LID",

  // --- ProjectDataDialog.tsx's Aquifers tab (AquifersEditor.tsx) ---
  "projectData.tabAquifers": "Acuíferos",
  "aquifer.addAquifer": "Añadir acuífero",
  "aquifer.deleteAquifer": "Eliminar acuífero",
  "aquifer.noAquiferSelected": "Selecciona un acuífero, o añade uno nuevo.",
  "aquifer.porosity": "Porosidad",
  "aquifer.wiltingPoint": "Punto de marchitez",
  "aquifer.fieldCapacity": "Capacidad de campo",
  "aquifer.conductivity": "Conductividad",
  "aquifer.conductSlope": "Pendiente de conductividad",
  "aquifer.tensionSlope": "Pendiente de succión",
  "aquifer.upperEvapFrac": "Fracción de evaporación superior",
  "aquifer.lowerEvapDepth": "Profundidad de evaporación inferior",
  "aquifer.gwRecession": "Recesión de agua subterránea",
  "aquifer.bottomElev": "Cota de fondo",
  "aquifer.waterTableElev": "Cota del nivel freático",
  "aquifer.upperMoisture": "Humedad de la zona superior",
  "aquifer.evapPattern": "Patrón de evaporación",
  "aquifer.evapPatternNone": "Ninguno",

  // --- PropertyPanel.tsx's groundwater sub-section (GroundwaterEditor.tsx) ---
  "groundwater.title": "Flujo subterráneo",
  "groundwater.noAquifersYet":
    "Añade primero un acuífero (Datos del proyecto → Acuíferos) para configurar esto.",
  "groundwater.aquifer": "Acuífero",
  "groundwater.node": "Nudo receptor",
  "groundwater.surfaceElev": "Cota de la superficie del terreno",
  "groundwater.a1": "Coeficiente de flujo subterráneo (A1)",
  "groundwater.a1Tooltip":
    "A1 en QL = A1(Hgw−Hcb)^B1 − A2(Hsw−Hcb)^B2 + A3·Hgw·Hsw — pondera la cota de la lámina freática del propio acuífero (Hgw).",
  "groundwater.b1": "Exponente de flujo subterráneo (B1)",
  "groundwater.b1Tooltip":
    "B1 de la misma fórmula de caudal lateral, aplicado a la cota de la lámina freática del acuífero (Hgw).",
  "groundwater.a2": "Coeficiente de flujo de agua superficial (A2)",
  "groundwater.a2Tooltip":
    "A2 de la misma fórmula — pondera la cota de la lámina de agua del nudo de vertido (Hsw), que se opone a la salida.",
  "groundwater.b2": "Exponente de flujo de agua superficial (B2)",
  "groundwater.b2Tooltip":
    "B2 de la misma fórmula, aplicado a la cota de la lámina de agua del nudo (Hsw).",
  "groundwater.a3": "Coeficiente de interacción superficie-subterráneo (A3)",
  "groundwater.a3Tooltip":
    "A3 de la misma fórmula — pondera el término de interacción directa Hgw·Hsw.",
  "groundwater.fixedDepth": "Profundidad de agua fija en el nudo receptor",
  "groundwater.nodeElev": "Cota de solera del nudo receptor (auto si vacío)",
  "groundwater.bottomElev": "Cota de fondo del acuífero, sobrescrita (auto si vacío)",
  "groundwater.waterTableElev": "Cota inicial del nivel freático, sobrescrita (auto si vacío)",
  "groundwater.upperMoisture": "Humedad inicial de la zona superior, sobrescrita (auto si vacío)",
  "groundwater.lateralFlow": "Expresión de flujo lateral (sobrescribe)",
  "groundwater.deepFlow": "Expresión de flujo profundo (sobrescribe)",

  // --- ProjectDataDialog.tsx's Snow Packs tab (SnowPacksEditor.tsx) ---
  "projectData.tabSnowPacks": "Paquetes de nieve",
  "snow.addPack": "Añadir paquete de nieve",
  "snow.deletePack": "Eliminar paquete de nieve",
  "snow.noPackSelected": "Selecciona un paquete de nieve, o añade uno nuevo.",
  "snow.plowable": "Área con quitanieves",
  "snow.impervious": "Área impermeable",
  "snow.pervious": "Área permeable",
  "snow.removal": "Retirada de nieve",
  "snow.cmin": "Coeficiente de fusión mínimo",
  "snow.cmax": "Coeficiente de fusión máximo",
  "snow.tbase": "Temperatura base de fusión",
  "snow.fwFrac": "Fracción de agua libre respecto al espesor",
  "snow.sd0": "Espesor inicial de nieve",
  "snow.fw0": "Agua libre inicial en el paquete",
  "snow.snn0OrSd100": "Espesor al 100% cobertura / área al 100% quitada",
  "snow.sdPlow": "Espesor a partir del cual se quita nieve",
  "snow.fout": "Fracción retirada fuera de la cuenca",
  "snow.fimperv": "Fracción retirada al área impermeable",
  "snow.fperv": "Fracción retirada al área permeable",
  "snow.fimelt": "Fracción convertida en fusión inmediata",
  "snow.fsubcatch": "Fracción retirada a otra subcuenca",
  "snow.toSubcatch": "Subcuenca receptora",

  // --- ProjectDataDialog.tsx's Climatology tab (ClimatologyEditor.tsx) ---
  "projectData.tabClimatology": "Climatología",
  "climate.jan": "Ene",
  "climate.feb": "Feb",
  "climate.mar": "Mar",
  "climate.apr": "Abr",
  "climate.may": "May",
  "climate.jun": "Jun",
  "climate.jul": "Jul",
  "climate.aug": "Ago",
  "climate.sep": "Sep",
  "climate.oct": "Oct",
  "climate.nov": "Nov",
  "climate.dec": "Dic",
  "climate.evaporation": "Evaporación",
  "climate.evapType": "Origen",
  "climate.evapTypeConstant": "Constante",
  "climate.evapTypeMonthly": "Media mensual",
  "climate.evapTypeTimeSeries": "Serie temporal",
  "climate.evapTypeFile": "Fichero climático",
  "climate.evapTypeTemperature": "Calculada a partir de la temperatura",
  "climate.evapConstantValue": "Valor",
  "climate.evapTimeSeries": "Serie temporal",
  "climate.recoveryPattern": "Patrón de recuperación",
  "climate.recoveryPatternNone": "Ninguno",
  "climate.dryOnly": "Aplicar evaporación solo en días secos",
  "climate.temperature": "Temperatura",
  "climate.tempSource": "Origen",
  "climate.tempSourceNone": "Ninguno",
  "climate.tempSourceTimeSeries": "Serie temporal",
  "climate.tempSourceFile": "Fichero climático",
  "climate.tempTimeSeries": "Serie temporal",
  "climate.tempFileName": "Nombre del fichero",
  "climate.tempFileStartDate": "Fecha de inicio (vacío para todo el fichero)",
  "climate.snowmelt": "Climatología de fusión de nieve",
  "climate.snowTemp": "Temperatura de nevada",
  "climate.tipm": "Peso del índice de temperatura antecedente",
  "climate.rnm": "Factor de recuperación de la curva ADC",
  "climate.elevation": "Elevación",
  "climate.latitude": "Latitud (grados)",
  "climate.dtLongitude": "Desfase horario respecto al huso local (h)",
  "climate.adjustments": "Ajustes mensuales",
  "climate.adjustTemperature": "Temperatura",
  "climate.adjustEvaporation": "Evaporación",
  "climate.adjustRainfall": "Lluvia (%)",
  "climate.adjustConductivity": "Multiplicador de conductividad",

  // --- ProjectDataDialog.tsx's Transects tab (TransectsEditor.tsx) ---
  "projectData.tabTransects": "Transectos",
  "transect.addTransect": "Añadir transecto",
  "transect.deleteTransect": "Eliminar transecto",
  "transect.noTransectSelected": "Selecciona un transecto, o añade uno nuevo.",
  "transect.nLeft": "Rugosidad margen izquierda (n)",
  "transect.nRight": "Rugosidad margen derecha (n)",
  "transect.nChannel": "Rugosidad del canal principal (n)",
  "transect.xLeftBank": "Estación de la margen izquierda",
  "transect.xRightBank": "Estación de la margen derecha",
  "transect.lengthFactor": "Modificador de longitud (meandro)",
  "transect.stationMultiplier": "Multiplicador de la distancia de estación",
  "transect.elevationOffset": "Desfase de elevación",
  "transect.station": "Estación",
  "transect.elevation": "Elevación",
  "transect.addStation": "Añadir estación",

  // --- PropertyPanel.tsx's DWF sub-section (DwfEditor.tsx) ---
  "dwf.title": "Caudal en tiempo seco",
  "dwf.noInflows": "Todavía no hay ninguna aportación en tiempo seco.",
  "dwf.constituent": "Constituyente",
  "dwf.avgValue": "Valor medio",
  "dwf.pattern1": "Patrón 1",
  "dwf.pattern2": "Patrón 2",
  "dwf.pattern3": "Patrón 3",
  "dwf.pattern4": "Patrón 4",
  "dwf.addInflow": "Añadir aportación en tiempo seco",
  "dwf.deleteInflow": "Quitar aportación en tiempo seco",

  // --- PropertyPanel.tsx's Inflows sub-section (InflowsEditor.tsx) ---
  "inflows.title": "Aportaciones directas",
  "inflows.noInflows": "Todavía no hay ninguna aportación directa.",
  "inflows.timeSeries": "Serie temporal",
  "inflows.type": "Tipo",
  "inflows.typeFlow": "Caudal",
  "inflows.typeConcen": "Concentración",
  "inflows.typeMass": "Masa",
  "inflows.unitsFactor": "Factor de unidades",
  "inflows.scaleFactor": "Factor de escala",
  "inflows.baseline": "Línea base",
  "inflows.basePattern": "Patrón de línea base",
  "inflows.addInflow": "Añadir aportación directa",
  "inflows.deleteInflow": "Quitar aportación directa",

  // --- ProjectDataDialog.tsx's Unit Hydrographs tab (UnitHydrographsEditor.tsx) ---
  "projectData.tabHydrographs": "Hidrogramas unitarios",
  "rdii.addHydrograph": "Añadir hidrograma unitario",
  "rdii.deleteHydrograph": "Eliminar hidrograma unitario",
  "rdii.noHydrographSelected": "Selecciona un hidrograma unitario, o añade uno nuevo.",
  "rdii.rainGage": "Pluviómetro",
  "rdii.monthAll": "Todos los meses",
  "rdii.deleteMonth": "Quitar mes",
  "rdii.addMonth": "Añadir mes",
  "rdii.responseShort": "Respuesta a corto plazo",
  "rdii.responseMedium": "Respuesta a medio plazo",
  "rdii.responseLong": "Respuesta a largo plazo",
  "rdii.r": "Fracción de lluvia que entra en la red (R)",
  "rdii.t": "Tiempo al pico (h)",
  "rdii.k": "Ratio de recesión",
  "rdii.iaMax": "Abstracción inicial máxima",
  "rdii.iaRecov": "Tasa de recuperación de la abstracción inicial",
  "rdii.iaInit": "Abstracción inicial ya rellena",

  // --- PropertyPanel.tsx's RDII sub-section (RdiiEditor.tsx) ---
  "rdii.inflowTitle": "Aportación RDII",
  "rdii.noHydrographsYet":
    "Añade primero un hidrograma unitario (Datos del proyecto → Hidrogramas unitarios) para configurar esto.",
  "rdii.unitHydrograph": "Hidrograma unitario",
  "rdii.sewerArea": "Área de alcantarillado que aporta RDII",

  // --- ProjectDataDialog.tsx's Control Rules tab (ControlRulesEditor.tsx) ---
  "projectData.tabControlRules": "Reglas de control",
  "controlRules.addRule": "Añadir regla",
  "controlRules.deleteRule": "Eliminar regla",
  "controlRules.noRuleSelected": "Selecciona una regla, o añade una nueva.",
  "controlRules.bodyHint": "Una cláusula por línea: IF/AND/OR/THEN/ELSE/PRIORITY.",
  "controlRules.visualMode": "Editor visual",
  "controlRules.textMode": "Editor de texto",
  "controlRules.cannotEditVisually":
    "El texto de esta regla usa una forma que el editor visual no puede representar (una referencia VARIABLE/EXPRESSION, u otra cosa no reconocida) — edítala como texto.",

  // --- ControlRuleBuilder.tsx (el formulario estructurado IF/THEN/ELSE) ---
  "controlRules.if": "SI",
  "controlRules.and": "Y",
  "controlRules.or": "O",
  "controlRules.then": "ENTONCES",
  "controlRules.else": "SI NO",
  "controlRules.ifSection": "Condiciones (SI)",
  "controlRules.thenSection": "Acciones (ENTONCES)",
  "controlRules.elseSection": "Acciones si no (SI NO)",
  "controlRules.addCondition": "Añadir condición",
  "controlRules.addAction": "Añadir acción",
  "controlRules.addElseAction": "Añadir acción SI NO",
  "controlRules.deleteCondition": "Eliminar condición",
  "controlRules.deleteAction": "Eliminar acción",
  "controlRules.priority": "Prioridad",
  "controlRules.compareToElement":
    "Comparar con el atributo de otro elemento en vez de un valor fijo",
  "controlRules.compareToValue": "Comparar con un valor fijo en vez del atributo de otro elemento",
  "controlRules.compareToKind": "Comparar con",
  "controlRules.rhsKindValue": "Valor",
  "controlRules.rhsKindElement": "Elemento",
  "controlRules.statusOpenOn": "Abierto / Encendido (1)",
  "controlRules.statusClosedOff": "Cerrado / Apagado (0)",
  "controlRules.settingNumber": "Valor fijo",
  "controlRules.settingCurve": "Curva",
  "controlRules.settingTimeSeries": "Serie temporal",
  "controlRules.objectPicker": "Tipo de objeto",
  "controlRules.idPicker": "Nombre del elemento",
  "controlRules.attributePicker": "Atributo",
  "controlRules.statusValue": "Valor de estado",
  "controlRules.comparisonValue": "Valor de comparación",
  "controlRules.timeValue": "Valor de hora",
  "controlRules.dateValue": "Valor de fecha",
  "controlRules.logicOperator": "Operador lógico",
  "controlRules.comparisonOperator": "Operador de comparación",
  "controlRules.settingKind": "Tipo de ajuste",
  "controlRules.settingValue": "Valor del ajuste",
  "controlRules.curvePicker": "Curva",
  "controlRules.timeSeriesPicker": "Serie temporal",

  // --- ProjectDataDialog.tsx's Streets tab (StreetsEditor.tsx) ---
  "projectData.tabStreets": "Calles",
  "projectData.addStreet": "Añadir calle",
  "projectData.deleteStreet": "Eliminar calle",
  "projectData.noStreetSelected": "Selecciona una sección de calle, o añade una nueva.",
  "projectData.streetRoadWidth": "Ancho de calzada (Tcrown)",
  "projectData.streetCurbHeight": "Altura de bordillo (Hcurb)",
  "projectData.streetCrossSlope": "Pendiente transversal (Sx), %",
  "projectData.streetRoughness": "Rugosidad de calzada",
  "projectData.streetGutterDepression": "Depresión de cuneta (a)",
  "projectData.streetGutterWidth": "Ancho de cuneta (W)",
  "projectData.streetSides": "Lados",
  "projectData.streetTwoSided": "Dos lados",
  "projectData.streetOneSided": "Un lado",
  "projectData.streetBackingWidth": "Ancho de trasdós (Tback)",
  "projectData.streetBackingSlope": "Pendiente de trasdós (Sback), %",
  "projectData.streetBackingRoughness": "Rugosidad de trasdós",

  // --- ProjectDataDialog.tsx's Inlets tab (InletsEditor.tsx) ---
  "projectData.tabInlets": "Sumideros",
  "projectData.addInlet": "Añadir sumidero",
  "projectData.deleteInlet": "Eliminar sumidero",
  "projectData.noInletSelected": "Selecciona un diseño de sumidero, o añade uno nuevo.",
  "projectData.inletType": "Tipo",
  "projectData.inletLength": "Longitud",
  "projectData.inletWidth": "Ancho",
  "projectData.inletHeight": "Altura",
  "projectData.inletGrateType": "Tipo de reja",
  "projectData.inletOpenAreaFraction": "Fracción de área abierta",
  "projectData.inletSplashVelocity": "Velocidad de salpicadura",
  "projectData.inletThroatAngle": "Ángulo de garganta",
  "projectData.inletCurveName": "Nombre de curva de captación",

  // --- PropertyPanel.tsx's Inlet Usage section (InletUsageEditor.tsx) ---
  "propertyPanel.inletUsage": "Sumideros",
  "propertyPanel.noInletUsage": "Este conducto todavía no tiene ningún sumidero.",
  "propertyPanel.addInletUsage": "Añadir sumidero",
  "propertyPanel.deleteInletUsage": "Quitar sumidero",
  "propertyPanel.inletUsageDesign": "Diseño de sumidero",
  "propertyPanel.inletUsageNode": "Nudo de captación",
  "propertyPanel.inletUsageNumInlets": "Nº de sumideros",
  "propertyPanel.inletUsagePctClogged": "% Obstruido",
  "propertyPanel.inletUsageFlowLimit": "Límite de caudal",
  "propertyPanel.inletUsageLocalDepress": "Depresión local",
  "propertyPanel.inletUsageLocalWidth": "Ancho local",
  "propertyPanel.inletUsagePlacement": "Colocación",
  "propertyPanel.noInletDesignsYet":
    "Añade primero un diseño de sumidero (Datos del proyecto → Sumideros) para colocarlo aquí.",
  "propertyPanel.invalidNumber": "No es un número — no se ha guardado.",

  // --- NetworkMap.tsx / GeoreferencedMap.tsx ---
  "networkMap.noCoordinates": "Este proyecto no tiene coordenadas de mapa que mostrar.",
  "networkMap.loadingNetwork": "Cargando red…",
  "networkMap.coordsGeographic": "Lon {lon}°  Lat {lat}°",
  "networkMap.coordsPlane": "X {x}  Y {y}",
  "networkMap.toggleOverview": "Mapa de conjunto",
  "networkMap.zoomIn": "Acercar",
  "networkMap.zoomOut": "Alejar",
  "networkMap.zoomToExtent": "Ver todo el proyecto",
  "networkMap.elementAriaLabel": "{kind} {name}",
  "map.showLinkDirection": "Mostrar dirección del flujo",

  // --- MapSettingsMenu.tsx ---
  "mapSettings.title": "Opciones de visualización del mapa",

  // --- ResultsChart.tsx ---
  "results.depth": "Profundidad",
  "results.head": "Carga",
  "results.volume": "Volumen",
  "results.totalInflow": "Entrada total",
  "results.flooding": "Inundación",
  "results.flow": "Caudal",
  "results.velocity": "Velocidad",
  "results.capacity": "Capacidad",
  "results.runoff": "Escorrentía",
  "results.gwFlow": "Flujo subterráneo",
  "results.precipitation": "Precipitación",
  "results.evaporation": "Evaporación",
  "results.infiltration": "Infiltración",
  "results.viewChart": "Gráfico",
  "results.viewTable": "Tabla",
  "results.time": "Hora",
  "simulationSummary.title": "Resumen de simulación",
  "simulationSummary.runoffContinuity": "Continuidad de escorrentía",
  "simulationSummary.totalPrecip": "Precipitación total",
  "simulationSummary.outfallRunon": "Escorrentía de retorno del vertido",
  "simulationSummary.evapLoss": "Pérdida por evaporación",
  "simulationSummary.infilLoss": "Pérdida por infiltración",
  "simulationSummary.surfaceRunoff": "Escorrentía superficial",
  "simulationSummary.initLidStorage": "Almacenamiento inicial en LID",
  "simulationSummary.lidDrainage": "Drenaje de LID",
  "simulationSummary.finalStorage": "Almacenamiento final",
  "simulationSummary.continuityError": "Error de continuidad",
  "simulationSummary.noRunoff": "No hay lluvia ni subcuencas de las que informar.",
  "simulationSummary.extremes": "Extremos",
  "simulationSummary.topNodeDepths": "Mayores profundidades de nudo",
  "simulationSummary.topLinkFlows": "Mayores caudales de enlace",
  "simulationSummary.node": "Nudo",
  "simulationSummary.link": "Enlace",
  "simulationSummary.noResults": "No hay resultados de los que informar.",
  "simulationSummary.peakSystemOutflow": "Vertido total máximo a los aliviaderos",
  "simulationSummary.exportCsv": "Exportar CSV",

  // --- StatisticsReportDialog.tsx ---
  "statistics.title": "Informe de Estadísticas",
  "statistics.category": "Categoría",
  "statistics.element": "Elemento",
  "statistics.variable": "Variable",
  "statistics.statType": "Estadístico",
  "statistics.statType.mean": "Media",
  "statistics.statType.peak": "Pico",
  "statistics.statType.total": "Total (volumen)",
  "statistics.statType.duration": "Duración",
  "statistics.minEventValue": "Valor mín. de evento",
  "statistics.minEventVolume": "Volumen mín. de evento",
  "statistics.minEventDelta": "Tiempo mín. entre eventos (horas)",
  "statistics.noResults": "Sin resultados de simulación para este elemento.",
  "statistics.summary": "Resumen",
  "statistics.eventCount": "Número de eventos",
  "statistics.eventFrequency": "Frecuencia de eventos",
  "statistics.min": "Mínimo",
  "statistics.max": "Máximo",
  "statistics.mean": "Media",
  "statistics.stdDev": "Desv. típica",
  "statistics.skew": "Asimetría",
  "statistics.period": "Periodo de análisis",
  "statistics.period.years": "años",
  "statistics.period.months": "meses",
  "statistics.events": "Eventos",
  "statistics.frequencyPlot": "Gráfico de Frecuencia",
  "statistics.noEvents": "No se encontraron eventos con los umbrales actuales.",
  "statistics.rank": "Rango",
  "statistics.startDate": "Fecha de inicio",
  "statistics.duration": "Duración (h)",
  "statistics.value": "Valor",
  "statistics.frequencyPercent": "Frecuencia (%)",
  "statistics.returnPeriod": "Periodo de retorno",

  "resultsAnimation.play": "Reproducir",
  "resultsAnimation.pause": "Pausar",
  "resultsAnimation.timeline": "Instante de la simulación",
  "resultsAnimation.showColor": "Mostrar los colores de {kind} en el mapa",
  "resultsAnimation.variablePicker": "Variable de color de {kind}",

  // --- ResultsDashboardPanel.tsx ---
  "resultsDashboard.title": "Dashboard de resultados",
  "resultsDashboard.addSelection": "Añadir selección",
  "resultsDashboard.clear": "Vaciar todo",
  "resultsDashboard.remove": "Quitar",
  "resultsDashboard.empty":
    "Selecciona uno o varios elementos en el mapa o el árbol y pulsa «Añadir selección».",
  "resultsDashboard.layoutCombined": "Combinado",
  "resultsDashboard.layoutGrid": "Cuadrícula",

  "longitudinalProfile.title": "Perfil longitudinal",
  "longitudinalProfile.titleWithNodes": "Perfil solera-terreno: {start} → {end}",
  "longitudinalProfile.startNode": "Nudo inicial",
  "longitudinalProfile.endNode": "Nudo final",
  "longitudinalProfile.pickOnMap": "Elegir en el mapa",
  "longitudinalProfile.hintPickStart": "Perfil longitudinal: haz clic en el nudo inicial",
  "longitudinalProfile.hintPickEnd": "Perfil longitudinal: haz clic en el nudo final",
  "longitudinalProfile.noPath": "Estos dos nudos no están conectados por ningún camino de la red.",
  "longitudinalProfile.distance": "Distancia",
  "longitudinalProfile.ground": "Terreno",
  "longitudinalProfile.invert": "Solera",
  "longitudinalProfile.waterSurface": "Lámina de agua",
  "networkTrace.traceDownstream": "Trazar aguas abajo desde nudo",
  "networkTrace.traceUpstream": "Trazar aguas arriba desde nudo",
  "networkTrace.hintPickDownstream": "Trazar aguas abajo: haz clic en el nudo de partida",
  "networkTrace.hintPickUpstream": "Trazar aguas arriba: haz clic en el nudo de partida",
  "networkTrace.resultDownstream": 'Aguas abajo de "{name}": {nodes} nudos, {links} enlaces',
  "networkTrace.resultUpstream": 'Aguas arriba de "{name}": {nodes} nudos, {links} enlaces',

  "thematicMap.pickAttribute": "Elige un atributo…",
  "thematicMap.categoryNode": "Nudo",
  "thematicMap.categoryLink": "Enlace",
  "thematicMap.categorySubcatchment": "Subcuenca",
  "thematicMap.nodeInvert": "Cota de solera",
  "thematicMap.nodeMaxDepth": "Profundidad máxima",
  "thematicMap.linkDiameter": "Diámetro",
  "thematicMap.linkLength": "Longitud",
  "thematicMap.linkRoughness": "Rugosidad",
  "thematicMap.subArea": "Área",
  "thematicMap.subImperv": "%Impermeable",
  "thematicMap.subWidth": "Ancho",
  "thematicMap.resultNodeMaxDepth": "Profundidad máxima (resultado)",
  "thematicMap.resultLinkMaxFlow": "Caudal máximo (resultado)",
  "thematicMap.attributePicker": "Atributo del color del mapa",
  "mapQuery.matchCount": "{count} de {total} coinciden",
  "mapQuery.selectMatches": "Seleccionar coincidencias",
  "mapQuery.attributePicker": "Atributo de la consulta",
  "mapQuery.comparator": "Comparador",
  "mapQuery.threshold": "Valor umbral",
  "backgroundImage.title": "Imagen de fondo",
  "backgroundImage.file": "Fichero de imagen",
  "backgroundImage.selectFile": "Seleccionar imagen…",
  "backgroundImage.noFileSelected": "Ningún fichero seleccionado",
  "backgroundImage.currentImage": "Imagen actual",
  "backgroundImage.noImage": "Sin imagen de fondo — solo se muestra en el modo Plano.",
  "backgroundImage.lowerLeftX": "X inferior izquierda",
  "backgroundImage.lowerLeftY": "Y inferior izquierda",
  "backgroundImage.upperRightX": "X superior derecha",
  "backgroundImage.upperRightY": "Y superior derecha",
  "backgroundImage.useProjectExtent": "Usar límites del proyecto",
  "backgroundImage.remove": "Quitar imagen",

  // --- ErrorBoundary.tsx ---
  "errorBoundary.title": "Algo ha ido mal",
  "errorBoundary.message":
    "Recarga la página para continuar. Tu fichero de proyecto no se ve afectado — todavía no se ha guardado nada.",
  "errorBoundary.detail": "Detalle técnico: {message}",
  "errorBoundary.reload": "Recargar",

  // --- Element kind labels (PropertyPanel.tsx's "selected.kind" line) ---
  "kind.junction": "Pozo",
  "kind.outfall": "Vertido",
  "kind.storage": "Almacenamiento",
  "kind.divider": "Divisor",
  "kind.conduit": "Conducto",
  "kind.pump": "Bomba",
  "kind.orifice": "Orificio",
  "kind.weir": "Vertedero",
  "kind.outlet": "Descarga",
  "kind.subcatchment": "Subcuenca",
  "kind.raingage": "Pluviómetro",

  // --- Property group section labels (PropertyPanel.tsx's group headers) ---
  "section.JUNCTIONS": "Pozo",
  "section.OUTFALLS": "Vertido",
  "section.STORAGE": "Almacenamiento",
  "section.DIVIDERS": "Divisor",
  "section.CONDUITS": "Conducto",
  "section.PUMPS": "Bomba",
  "section.ORIFICES": "Orificio",
  "section.WEIRS": "Vertedero",
  "section.OUTLETS": "Descarga",
  "section.XSECTIONS": "Sección transversal",
  "section.SUBCATCHMENTS": "Subcuenca",
  "section.SUBAREAS": "Subáreas",
  "section.INFILTRATION_HORTON": "Infiltración (Horton)",
  "section.INFILTRATION_MODIFIED_HORTON": "Infiltración (Horton modificado)",
  "section.INFILTRATION_GREEN_AMPT": "Infiltración (Green Ampt)",
  "section.INFILTRATION_MODIFIED_GREEN_AMPT": "Infiltración (Green Ampt modificado)",
  "section.INFILTRATION_CURVE_NUMBER": "Infiltración (Número de curva)",
  "section.RAINGAGES": "Pluviómetro",

  // --- Property field (column) labels — SWMM's own .inp vocabulary, see fieldLabels.ts ---
  "field.invertElevation": "Cota de solera",
  "field.maxDepth": "Profundidad máxima",
  "field.initDepth": "Profundidad inicial",
  "field.surchargeDepth": "Profundidad de sobrecarga",
  "field.pondedArea": "Área de encharcamiento",
  "field.outfallType": "Tipo de vertido",
  "field.stageTableTimeseries": "Nivel/Tabla/Serie temporal",
  "field.tideGate": "Compuerta de marea",
  "field.shape": "Forma",
  "field.coeffCurveA": "Coef./Curva A",
  "field.coeffB": "Coef. B",
  "field.coeffC": "Coef. C",
  "field.evapFraction": "Fracción de evaporación",
  "field.suctionHead": "Carga de succión",
  "field.conductivity": "Conductividad",
  "field.initialDeficit": "Déficit inicial",
  "field.elevation": "Cota",
  "field.divertedLink": "Conducto derivado",
  "field.dividerType": "Tipo de divisor",
  "field.param1": "Parámetro 1",
  "field.param2": "Parámetro 2",
  "field.param3": "Parámetro 3",
  "field.param4": "Parámetro 4",
  "field.param5": "Parámetro 5",
  "field.fromNode": "Nudo inicial",
  "field.toNode": "Nudo final",
  "field.length": "Longitud",
  "field.roughness": "Rugosidad",
  "field.inletOffset": "Desnivel de entrada",
  "field.outletOffset": "Desnivel de salida",
  "field.initFlow": "Caudal inicial",
  "field.maxFlow": "Caudal máximo",
  "field.pumpCurve": "Curva de bomba",
  "field.status": "Estado",
  "field.startupDepth": "Profundidad de arranque",
  "field.shutoffDepth": "Profundidad de parada",
  "field.orificeType": "Tipo de orificio",
  "field.crestHeight": "Altura de cresta",
  "field.dischargeCoeff": "Coef. de descarga",
  "field.flapGate": "Válvula de clapeta",
  "field.openCloseTime": "Tiempo de apertura/cierre",
  "field.weirType": "Tipo de vertedero",
  "field.endContractions": "Contracciones en los extremos",
  "field.endCoeff": "Coef. en los extremos",
  "field.ratingType": "Tipo de curva de descarga",
  "field.geom1": "Geom1",
  "field.geom2": "Geom2",
  "field.geom3": "Geom3",
  "field.geom4": "Geom4",
  "field.barrels": "Barriles",
  "field.culvertCode": "Código de alcantarilla",
  "field.raingage": "Pluviómetro",
  "field.subcatchmentOutlet": "Punto de vertido",
  "field.area": "Área",
  "field.percentImperv": "%Imperm.",
  "field.width": "Ancho",
  "field.percentSlope": "%Pendiente",
  "field.curbLength": "Longitud de bordillo",
  "field.snowPack": "Manto de nieve",
  "field.nImperv": "N-Imperm.",
  "field.nPerv": "N-Perm.",
  "field.sImperv": "S-Imperm.",
  "field.sPerv": "S-Perm.",
  "field.percentZeroImperv": "%Imperm. sin depresión",
  "field.routeTo": "Enrutar a",
  "field.percentRouted": "%Enrutado",
  "field.maxRate": "Tasa máxima",
  "field.minRate": "Tasa mínima",
  "field.decayConstant": "Constante de decaimiento",
  "field.dryTime": "Tiempo de secado",
  "field.maxInfiltration": "Infiltración máxima",
  "field.curveNumber": "Número de curva",
  "field.dryingTime": "Tiempo de secado",
  "field.format": "Formato",
  "field.interval": "Intervalo",
  "field.scf": "SCF",
  "field.sourceType": "Tipo de fuente",
  "field.sourceName": "Nombre de fuente",

  // --- fieldLabels.ts's OPTION_LABEL_KEYS (real closed-set dropdown values, sectionSchema.ts's own COLUMN_OPTIONS) ---
  "option.subareaOutlet": "Salida",
  "option.imperv": "Superficie impermeable",
  "option.perv": "Superficie permeable",
  "option.off": "Apagada",
  "option.on": "Encendida",
  "option.dummy": "Ficticia",
  "option.circular": "Circular",
  "option.filledCircular": "Circular rellena",
  "option.rectClosed": "Rectangular cerrada",
  "option.rectOpen": "Rectangular abierta",
  "option.trapezoidal": "Trapezoidal",
  "option.triangular": "Triangular",
  "option.parabolic": "Parabólica",
  "option.power": "Potencial",
  "option.rectTriangular": "Rectangular-triangular",
  "option.rectRound": "Rectangular-circular",
  "option.modBasketHandle": "Asa de cesta modificada",
  "option.horizEllipse": "Elíptica horizontal",
  "option.vertEllipse": "Elíptica vertical",
  "option.arch": "Arco",
  "option.egg": "Ovoide",
  "option.horseshoe": "Herradura",
  "option.gothic": "Gótica",
  "option.catenary": "Catenaria",
  "option.semielliptical": "Semielíptica",
  "option.basketHandle": "Asa de cesta",
  "option.semicircular": "Semicircular",
  "option.irregular": "Irregular",
  "option.custom": "Personalizada",
  "option.forceMain": "Impulsión",
  "option.street": "Calle",
  "option.cylindrical": "Cilíndrica",
  "option.conical": "Cónica",
  "option.pyramidal": "Piramidal",
  "option.cutoff": "Corte",
  "option.weir": "Vertedero",
  "option.overflow": "Rebose",
  "option.tabular": "Tabular",
  "option.functional": "Funcional",
  "option.no": "No",
  "option.yes": "Sí",
  "option.side": "Lateral",
  "option.bottom": "Fondo",
  "option.transverse": "Transversal",
  "option.sideflow": "De flujo lateral",
  "option.vNotch": "En V",
  "option.roadway": "De calzada",
  "option.free": "Libre",
  "option.normal": "Normal",
  "option.fixed": "Fijo",
  "option.tidal": "Mareal",
  "option.timeseries": "Serie temporal",
  "option.tabularDepth": "Tabular/Profundidad",
  "option.tabularHead": "Tabular/Carga",
  "option.functionalDepth": "Funcional/Profundidad",
  "option.functionalHead": "Funcional/Carga",
  "option.intensity": "Intensidad",
  "option.volume": "Volumen",
  "option.cumulative": "Acumulado",
  "option.file": "Fichero",
  "option.none": "Ninguno",
  "option.grate": "Rejilla",
  "option.curb": "Bordillo",
  "option.slotted": "Ranurado",
  "option.dropGrate": "Rejilla de caída",
  "option.dropCurb": "Bordillo de caída",
  "option.storage": "Almacenamiento",
  "option.rating": "Tabla de gasto",
  "option.control": "Control",
  "option.diversion": "Desvío",
  "option.shape": "Forma",
  "option.monthly": "Mensual",
  "option.daily": "Diario",
  "option.hourly": "Horario",
  "option.weekend": "Fin de semana",
  "option.exponential": "Exponencial",
  "option.saturation": "Saturación",
  "option.external": "Externa",
  "option.eventMeanConcentration": "Concentración media del evento",
  "option.area": "Área",

  // --- engineWarnings.ts (SimulationResults.warnings translations) ---
  "engineWarning.tooFewColumns":
    '{kind} "{name}" tiene muy pocas columnas para resolver su {what} y no se modela.',
  "engineWarning.conduitUnsupportedShape":
    'El conducto "{name}" no se modela: la forma de sección "{shape}" no está soportada.',
  "engineWarning.conduitInvalidLength":
    'El conducto "{name}" tiene una Length ({length}) inválida y no se modela.',
  "engineWarning.conduitInvalidManningN":
    'El conducto "{name}" tiene un Manning\'s n ({manningN}) inválido y no se modela.',
  "engineWarning.outletUndefinedCurve":
    'La descarga (Outlet) "{name}" no se modela: su curva TABULAR "{curve}" no está definida.',
  "engineWarning.outfallTidalCurveUndefined":
    'El vertido (Outfall) "{name}" es TIDAL pero su curva "{curve}" no está definida — se trata como FREE en su lugar.',
  "engineWarning.outfallFixedInvalidStage":
    'El vertido (Outfall) "{name}" es FIXED pero su valor de nivel "{stage}" no es un número real — no se modela como contorno externo.',
  "engineWarning.outfallTimeseriesUndefined":
    'El vertido (Outfall) "{name}" es TIMESERIES pero su serie "{series}" no está definida o está vacía — no se modela como contorno externo.',
  "engineWarning.outfallUnrecognizedType":
    'El vertido (Outfall) "{name}" tiene un tipo no reconocido ("{type}") — se trata como un nudo interior ordinario en lugar de un contorno externo.',
  "engineWarning.crownElevationMissingNode":
    '{kind} "{name}" hace referencia al nudo "{node}", que no existe — se omite su contribución a la elevación de clave.',
  "engineWarning.normalFlowLimitedUnrecognized":
    '[OPTIONS] NORMAL_FLOW_LIMITED tiene un valor no reconocido ("{value}") — se usa el valor por defecto ({default}) en su lugar.',
  "engineWarning.invalidNumberUnset":
    '{context}: "{token}" no es un número válido — se trata como no definido ("*").',
  "engineWarning.resultsResampled":
    "Esta simulación produjo {totalPeriods} periodos de informe, más que el límite de visualización de {maxPoints} puntos — los resultados se remuestrearon con un intervalo {stride} veces más ancho a lo largo de TODA la simulación, en lugar de cortarse a mitad de camino.",
  "engineWarning.gwfDeepExpressionInvalid":
    'La expresión [GWF] DEEP de la subcuenca "{subcatchment}" no se pudo interpretar y se descartó — se usa la fórmula de flujo profundo por defecto en su lugar.',
  "engineWarning.gwfLateralExpressionInvalid":
    'La expresión [GWF] LATERAL de la subcuenca "{subcatchment}" no se pudo interpretar y se descartó — no se añade nada al flujo lateral por defecto.',
  "engineWarning.groundwaterMissingAquifer":
    'La subcuenca "{subcatchment}" de [GROUNDWATER] hace referencia al acuífero "{aquifer}", que no tiene una fila [AQUIFERS] correspondiente — el agua subterránea no se modela para ella.',
  "engineWarning.inletNotConduit":
    'El sumidero (Inlet) en "{link}" no se modela: no es un conducto modelado.',
  "engineWarning.inletDesignUndefined":
    'El sumidero (Inlet) en "{link}" no se modela: su diseño "{inlet}" no está definido o no está soportado (CUSTOM).',
  "engineWarning.inletDesignInvalidShape":
    'El sumidero (Inlet) en "{link}" no se modela: su diseño "{inlet}" ({designKind}) no es válido para la forma de sección de este conducto.',
  "engineWarning.lidUsageUnknownControl":
    '[LID_USAGE] "{subcatchment}" hace referencia al control LID "{control}", que no tiene una fila [LID_CONTROLS] correspondiente o tiene un tipo no soportado — esta unidad LID no se modela.',
  "engineWarning.lidMissingLayerRow":
    '[LID_CONTROLS] "{name}" ({type}) no tiene fila {layer} — esta unidad LID no se modela.',
  "engineWarning.lidMissingPavementRow":
    '[LID_CONTROLS] "{name}" (POROUS_PAVEMENT) no tiene fila PAVEMENT, o su Thickness es 0 — esta unidad LID no se modela.',
  "engineWarning.lidUsageInvalidNumberOrArea":
    '[LID_USAGE] "{subcatchment}" / "{control}" tiene Number<=0 o Area<=0 — esta unidad LID no se modela.',
  "engineWarning.raingageMissingInterval":
    '[RAINGAGES] "{name}" tiene un Interval ausente o cero — reportará lluvia cero en todo momento, aunque tiene una fuente de lluvia real.',
  "engineWarning.rdiiUnknownHydrograph":
    '[RDII] hace referencia al hidrograma unitario "{name}", que no tiene una fila [HYDROGRAPHS] correspondiente — se ignora cada nudo [RDII] que lo usa.',
  "engineWarning.hydrographMissingRainGage":
    '[HYDROGRAPHS] "{name}" no tiene pluviómetro asignado — se ignora cada nudo [RDII] que lo usa.',
  "engineWarning.hydrographUnknownRainGage":
    '[HYDROGRAPHS] "{name}" hace referencia al pluviómetro "{rainGage}", que no tiene una fila [RAINGAGES] correspondiente — se ignora cada nudo [RDII] que lo usa.',
  "engineWarning.invalidNumberFallback":
    '{context}: "{token}" no es un número válido — se usa {fallback} en su lugar.',
  "engineWarning.inletOnSagUnsupported":
    'El sumidero (Inlet) en "{link}" no se modela: la captación en sumidero de punto bajo (on-sag) no está soportada.',
  "engineWarning.inletDropCurbUnsupportedKW":
    'El sumidero (Inlet) en "{link}" no se modela: la captación DROP_CURB no está soportada en Onda Cinemática/Flujo Uniforme.',
  "engineWarning.dividerUnsupportedType":
    'El divisor (Divider) "{name}" no se modela: solo se admiten los tipos CUTOFF, WEIR, TABULAR y OVERFLOW (se encontró "{type}").',
  "engineWarning.controlsExpressionInvalid":
    'La EXPRESSION "{name}" de [CONTROLS] no se pudo interpretar y se descartó — toda premisa que la referencia se resuelve como indefinida.',
  "engineWarning.controlsRuleUnsupported":
    'La regla [CONTROLS] "{name}" usa una forma que la Fase 1 no soporta y se descartó (ver la propia cabecera de controlRuleParser.ts para lo que está cubierto).',
  "engineWarning.controlsNoRulesEvaluated":
    "Hay reglas [CONTROLS] presentes pero ninguna se pudo evaluar — todas usan una forma que la Fase 1 no soporta (ver la propia cabecera de controlRuleParser.ts para lo que está cubierto).",
  "engineWarning.inflowsPollutantUnsupported":
    "[INFLOWS] tiene una fila de contaminante (CONCEN/MASS), que no se modela: bajo Onda Cinemática/Flujo Uniforme, el enrutamiento de calidad de agua de este puerto es solo Onda Dinámica; bajo Onda Dinámica, el Constituent de esta fila no coincide con ningún nombre de [POLLUTANTS]. El caudal externo propio de ese contaminante se ignora.",
  "engineWarning.dwfPollutantUnsupported":
    "[DWF] tiene una fila de concentración de contaminante, que no se modela: el soporte de caudal en tiempo seco de este puerto es solo FLOW. La carga base de contaminante en tiempo seco se ignora.",
  "engineWarning.raingageFileUnsupported":
    "[RAINGAGES] tiene un pluviómetro con fuente FILE, que no se modela: reporta lluvia cero en todo momento.",
  "engineWarning.raingageTimeseriesFileUnsupported":
    "[RAINGAGES] usa una [TIMESERIES] que a su vez tiene fuente FILE, que no se modela: reporta lluvia cero en todo momento.",
  "engineWarning.evaporationFileUnsupported":
    "[EVAPORATION] FILE no se modela: la evaporación potencial es cero en todo momento en lugar de leerse de un fichero climático externo.",
  "engineWarning.temperatureFileUnsupported":
    "[TEMPERATURE] FILE no se modela: la temperatura del aire (y cualquier efecto de nieve/evaporación derivado de la temperatura) no se lee de un fichero climático externo.",
  "engineWarning.storageUnsupportedShape":
    'El nudo de almacenamiento "{name}" no se modela: solo se admiten las formas FUNCTIONAL, CYLINDRICAL, CONICAL, PARABOLIC, PYRAMIDAL y TABULAR (se encontró "{shape}").',
  "engineWarning.storageInvalidShapeCoeffs":
    'El nudo de almacenamiento "{name}" ({shape}) tiene un coeficiente de forma no válido o fuera de rango ({y0}, {y1}, {y2}) y no se modela.',
  "engineWarning.orificeUnsupportedConfig":
    'El orificio "{name}" no se modela: solo se admiten orificios SIDE o BOTTOM con sección CIRCULAR o RECT_CLOSED.',
  "engineWarning.weirUnsupportedConfig":
    'El vertedero "{name}" no se modela: solo se admiten vertederos TRANSVERSE/SIDEFLOW/ROADWAY con sección RECT_OPEN, V-NOTCH con sección TRIANGULAR, o TRAPEZOIDAL con sección TRAPEZOIDAL.',
  "engineWarning.pumpUnsupportedCurveType":
    'La bomba "{name}" no se modela: solo se admiten curvas de tipo PUMP1/PUMP2/PUMP3/PUMP4/PUMP5.',
  "engineWarning.pollutantUnknownCoPollutant":
    '[POLLUTANTS] "{name}" hace referencia al co-contaminante "{coPollutant}", que no tiene una fila [POLLUTANTS] correspondiente — se trata como si no tuviera co-contaminante.',
  "engineWarning.landUseUnknown":
    '[{section}] hace referencia al uso del suelo "{landUse}", que no tiene una fila [LANDUSES] correspondiente — se ignora.',
  "engineWarning.coveragesUnknownLandUse":
    'La subcuenca "{subcatchment}" de [COVERAGES] hace referencia al uso del suelo "{landUse}", que no tiene una fila [LANDUSES] correspondiente — se ignora.',
  "engineWarning.treatmentUnknownPollutant":
    'El nudo "{node}" de [TREATMENT] hace referencia al contaminante "{pollutant}", que no tiene una fila [POLLUTANTS] correspondiente — esa fila se ignora.',
  "engineWarning.treatmentInvalidExpression":
    'El nudo "{node}" de [TREATMENT], contaminante "{pollutant}", tiene una expresión malformada y se descartó — no se aplica tratamiento para él ahí.',
  "engineWarning.subcatchmentMissingRow":
    'La subcuenca "{name}" no se modela: no tiene una fila [SUBAREAS]/[INFILTRATION] correspondiente.',
  "engineWarning.dwStepsNotConverged":
    "Onda dinámica: el {percent}% de los pasos de tiempo alcanzaron el número máximo de iteraciones sin converger — revise si hay un elemento inestable o mal configurado.",
  "engineWarning.dividerUnknownLink":
    'El divisor "{name}" no se modela: su enlace de derivación "{link}" no es un conducto modelado.',

  // --- textRegistry.ts (model/inp/*.ts warnings translations) ---
  "modelWarning.nonFiniteCoordinate":
    'La fila "{name}" de [{section}] tiene una coordenada no finita ({x}, {y}).',
  "modelWarning.malformedLinkRow":
    'La fila "{name}" de [{section}] no tiene sus dos nudos (origen/destino).',
};
